import {
  FetchError,
  ReferredVolumeResponse,
  ReferredVolumeResponseSchema,
} from "@decibeltrade/sdk";
import { type FetchQueryOptions, useQuery } from "@tanstack/react-query";

import type { GetAuthHeaders } from "@/utils/signature-auth";

import { useWriteDexSdk } from "../write-dex-sdk-context";
import { fetchAffiliateRead } from "./affiliate-read";

export const REFERRED_VOLUME_QUERY_KEY = "referredVolume";

export interface ReferredVolumeQueryArgs {
  referrerAccount: string | undefined;
  /** Most recent UTC days. Server defaults to 14 and clamps to 90. */
  days?: number;
  /**
   * Supplied by the hook, not the caller: `X-Subaccount-Address` is what lets the backend notice a
   * stale session cookie after a wallet switch, and without it wallet A's data can land in wallet
   * B's cache. See `affiliate-read.ts`.
   */
  getAuthHeaders?: GetAuthHeaders;
  subaccountAddress?: string;
}

export function referredVolumeQueryKey({ referrerAccount, days }: ReferredVolumeQueryArgs) {
  return [REFERRED_VOLUME_QUERY_KEY, referrerAccount, days] as const;
}

export function referredVolumeQuery({
  referrerAccount,
  days,
  getAuthHeaders,
  subaccountAddress,
}: ReferredVolumeQueryArgs) {
  return {
    queryKey: referredVolumeQueryKey({ referrerAccount, days }),
    queryFn: async () => {
      if (!referrerAccount) return [];

      try {
        return await fetchAffiliateRead({
          endpoint: "volume-daily",
          schema: ReferredVolumeResponseSchema,
          params: { days },
          getAuthHeaders,
          subaccountAddress,
        });
      } catch (error) {
        // A network that has not traded has no rows, not a failure.
        if (error instanceof FetchError && error.status === 404) return [];
        throw error;
      }
    },
    retry(failureCount: number, error: Error) {
      if (error instanceof FetchError && error.status === 404) return false;
      return failureCount < 3;
    },
  } satisfies FetchQueryOptions<ReferredVolumeResponse>;
}

export function useReferredVolumeQuery(args: ReferredVolumeQueryArgs) {
  const { getAuthHeaders, subaccountAddress } = useWriteDexSdk();
  return useQuery({
    ...referredVolumeQuery({ ...args, getAuthHeaders, subaccountAddress }),
    // Waits for the session, like every consumer of signatureAuthMiddleware: an
    // unauthenticated read is a guaranteed 401 whose SIWA retry cannot run, and the
    // query key carries no auth, so nothing would refetch when the session hydrates.
    enabled: !!args.referrerAccount && !!getAuthHeaders,
  });
}
